import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { EntryPoint, EntryPointService, GetAllLayersParams, LayerService } from '@mirage-projects/cw-commons';
import { Layer, LayerAdapterService, PcvizLayer, Project, ResultPage } from '@mirage-projects/cw-models';

/**
 * Service to interact with the Layers backend
 */
@Injectable({
    providedIn: 'root',
})
export class AppLayerService implements LayerService {
    constructor(
        private http: HttpClient,
        private layerAdapter: LayerAdapterService,
        private entryPointService: EntryPointService
    ) {}

    /**
     * Get layer by id
     * @param id Layer id
     */
    get(id: number): Observable<Layer> {
        const url = this.entryPointService.getResource(EntryPoint.LAYERS, `${id}`);

        return this.http.get<Layer>(`${url}`).pipe(
            map((response: any): Layer => this.layerAdapter.adapt(response)),
            catchError((err: any) => throwError(err))
        );
    }

    /**
     * Get the list of layers
     * @param params Parameters to filter the list
     */
    getAll(params: Partial<GetAllLayersParams> = {}): Observable<ResultPage<Layer>> {
        const url = this.entryPointService.getResource(EntryPoint.LAYERS);

        return this.http.get<ResultPage<Layer>>(`${url}`, { params }).pipe(
            map((response: any): any => this.layerAdapter.adapt(response)),
            catchError((err: any) => throwError(err))
        );
    }

    /**
     * Delete a layer
     * @param layer The layer to delete
     */
    delete(layer: Layer | PcvizLayer | { id: number }): Observable<Layer | undefined> {
        const url = this.entryPointService.getResource(EntryPoint.LAYERS, `${layer.id}`);

        return this.http.delete<Layer>(`${url}`).pipe(
            map((response: any): Layer | undefined => response?.data),
            catchError((err: any) => throwError(err))
        );
    }

    /**
     * Update a layer
     * @param layer The layer to update
     */
    update(layer: Layer): Observable<Layer> {
        const url = this.entryPointService.getResource(EntryPoint.LAYERS, `${layer.id}`);

        return this.http
            .patch(`${url}`, {
                name: layer.name,
                description: layer.description,
                horizProjectionEPSG: layer.horizProjectionEPSG,
            })
            .pipe(
                map((response: any): Layer => this.layerAdapter.adapt(response.data)),
                catchError((err: any) => throwError(err))
            );
    }

    /**
     * Rename a layer
     * @param layer Layer to rename
     * @param newName The new name
     */
    rename(layer: Layer, newName: string): Observable<Layer> {
        const url = this.entryPointService.getResource(EntryPoint.LAYERS, `${layer.id}`);

        return this.http
            .patch(`${url}`, {
                name: newName,
            })
            .pipe(
                map((response: any): Layer => this.layerAdapter.adapt(response.data)),
                catchError((err: any) => throwError(err))
            );
    }

    getLayerProjectReferences(id: number): Observable<ResultPage<Project>> {
        const url = this.entryPointService.getResource(EntryPoint.PROJECTS);

        return this.http.get<ResultPage<Project>>(`${url}?layer=${id}`);
    }

    deleteList(layerIds: number[]): Observable<void> {
        const url = `${this.entryPointService.getResource(EntryPoint.LAYERS)}delete-layers`;

        return this.http.delete<void>(url, { body: { layerIds } });
    }
}
